import { ArrowUpDown, CheckCircle2, FileSignature, Scale } from "lucide-react";
import InfoContentSection from "./InfoContentSection";
import { formatLabelList } from "@cle/utils/injunctionReportNotices";
import { MAX_TOTAL_TRANSFER_LESSONS } from "@cle/utils/calculateReportCategorySummaries";

export type ReportRuleCategory = {
  label: string;
  /** Udeladt = ingen grænse (kursusdeltagelse). */
  maxReported?: number;
  canTransferLessons: boolean;
};

type ReportRulesInfoContentProps = {
  categories: ReportRuleCategory[];
  /** Efteruddannelsesperioden medtager dispensationer automatisk; afgørelser gør ikke. */
  includesAutomaticDispensations?: boolean;
};

/**
 * Reglerne bag indberetningen, forklaret for medlemmet.
 *
 * Sætningerne bygges ud fra den kategorikonfiguration, kalderen faktisk regner
 * med, i stedet for at gentage tallene i prosa. De to flows har ikke samme
 * kategorier — efteruddannelsesperioden har dispensation, afgørelser har ikke —
 * og en håndskrevet tekst ville før eller siden komme til at love det forkerte i
 * det ene af dem.
 */
const ReportRulesInfoContent = ({
  categories,
  includesAutomaticDispensations = false,
}: ReportRulesInfoContentProps) => {
  const capped = categories.filter(
    (category) => category.maxReported !== undefined,
  );
  const uncapped = categories.filter(
    (category) => category.maxReported === undefined,
  );
  const transferable = categories.filter(
    (category) => category.canTransferLessons,
  );
  const nonTransferable = categories.filter(
    (category) => !category.canTransferLessons,
  );

  // "Undervisning tæller med op til 27 lektioner, forfatterskab op til 18 og
  // dispensation op til 54." — verbet står kun i første led, så sætningen kan
  // læses højt uden at hakke.
  const capsSentence = capped
    .map((category, index) =>
      index === 0
        ? `${category.label} tæller med op til ${category.maxReported} lektioner`
        : `${category.label.toLowerCase()} op til ${category.maxReported}`,
    )
    .reduce((sentence, part, index, parts) => {
      if (index === 0) {
        return part;
      }

      return index === parts.length - 1
        ? `${sentence} og ${part}`
        : `${sentence}, ${part}`;
    }, "")
    .concat(".");

  return (
    <div className="d-flex flex-column gap-3">
      <InfoContentSection
        icon={CheckCircle2}
        title="Kun godkendt dokumentation kan vælges"
      >
        Du kan kun vælge dokumentation, der er godkendt og ligger inden for
        perioden.
        {includesAutomaticDispensations
          ? " Dispensationer for perioden tælles med automatisk og kan ikke fravælges."
          : ""}
      </InfoContentSection>

      <InfoContentSection icon={Scale} title="Hver kategori har et maksimum">
        {capsSentence}
        {uncapped.length > 0 ? (
          <>
            {" "}
            For {formatLabelList(uncapped.map((c) => c.label))} er der ingen
            grænse.
          </>
        ) : null}{" "}
        Vælger du mere end en kategoris maksimum, kan de overskydende lektioner
        ikke bruges.
      </InfoContentSection>

      <InfoContentSection
        icon={ArrowUpDown}
        title="Overskud kan overføres — men ikke fra alle kategorier"
      >
        Har du flere lektioner, end lektionskravet kræver, kan overskud fra{" "}
        {formatLabelList(transferable.map((c) => c.label))} overføres til næste
        periode — højst {MAX_TOTAL_TRANSFER_LESSONS} lektioner i alt.
        {nonTransferable.length > 0 ? (
          <>
            {" "}
            Lektioner fra {formatLabelList(
              nonTransferable.map((c) => c.label),
            )}{" "}
            kan ikke overføres. De bliver indberettet nu, fordi dokumentationen
            ikke kan deles op.
          </>
        ) : null}
      </InfoContentSection>

      <InfoContentSection icon={FileSignature} title="Til sidst signerer du">
        På det sidste trin ser du en opsummering. Er tallene rigtige, godkender
        du indberetningen og signerer med MitID. Indtil da kan du gå tilbage og
        rette dine valg.
        {/* TODO(sekretariatet): kan en indberetning ændres efter signering,
            eller skal medlemmet kontakte sekretariatet? */}
      </InfoContentSection>
    </div>
  );
};

export default ReportRulesInfoContent;
